import { createTypedHooks, createStore, Action, action } from "easy-peasy";
import {
  ApplicationState,
  AuthenticationState,
  GeoInferenceEndpoint,
  Campaign,
} from "./models";

export interface ApplicationActions {
  setCurrentCampaign: Action<ApplicationState, Campaign>;
  // Geo Campaigns
  setCampaigns: Action<ApplicationState, Campaign[]>;
  createCampaign: Action<ApplicationState, Campaign>;
  updateCampaign: Action<
    ApplicationState,
    {
      id: string;
      campaign: Partial<Campaign>;
    }
  >;
  deleteCampaign: Action<ApplicationState, string>;

  // Selected campaign/prefiltering job/labeling job
  setSelectedCampaignJobName: Action<ApplicationState, string | undefined>;
  setSelectedCampaignPrefilteringJobName: Action<
    ApplicationState,
    string | undefined
  >;
  setSelectedGeoLabelingJobName: Action<ApplicationState, string | undefined>;

  // Geo Inference Endpoints
  setGeoInferenceEndpoints: Action<ApplicationState, GeoInferenceEndpoint[]>;
  addGeoInferenceEndpoint: Action<ApplicationState, GeoInferenceEndpoint>;
  setAuthenticationState: Action<ApplicationState, AuthenticationState>;
}

export type ApplicationStateAndActions = ApplicationState & ApplicationActions;

// Initial (empty) application state
const applicationState: ApplicationState = {
  campaigns: {
    byJobName: {},
    JobNames: [],
  },
  geoLabelingJobs: {
    byJobName: {},
    JobNames: [],
  },
  campaignPrefilteringJobs: {
    byJobName: {},
    JobNames: [],
  },
  geoInferenceEndpoints: {
    byId: {},
    ids: [],
  },
  currentCampaign: undefined,
  selectedCampaignJobName: undefined,
  selectedCampaignPrefilteringJobName: undefined,
  selectedGeoLabelingJobName: undefined,
  internal: {
    auth: {
      isAuthenticated: false,
    },
  },
};

// Action definitions
const applicationActions: ApplicationActions = {
  // JobDetails page: current geo labeling job configuration
  setCurrentCampaign: action((state, currentGeoLabelingJobConfiguration) => {
    state.currentCampaign = currentGeoLabelingJobConfiguration;
  }),

  // List Jobs page: table of job summaries
  setCampaigns: action((state, campaigns) => {
    state.campaigns.byJobName = {};
    state.campaigns.JobNames = [];
    campaigns.forEach((campaign) => {
      state.campaigns.byJobName[campaign.JobName] = campaign;
      state.campaigns.JobNames.push(campaign.JobName);
    });
  }),
  createCampaign: action((state, campaign) => {
    state.campaigns.byJobName[campaign.JobName] = campaign;
    state.campaigns.JobNames.push(campaign.JobName);
  }),
  updateCampaign: action((state, { id, campaign }) => {
    state.campaigns.byJobName[id] = {
      ...state.campaigns.byJobName[id],
      ...campaign,
    };
  }),
  deleteCampaign: action((state, id) => {
    delete state.campaigns.byJobName[id];
    const index = state.campaigns.JobNames.indexOf(id);
    state.campaigns.JobNames.splice(index, 1);
  }),

  setSelectedCampaignJobName: action((state, jobName) => {
    state.selectedCampaignJobName = jobName;
  }),

  setSelectedCampaignPrefilteringJobName: action((state, jobName) => {
    state.selectedCampaignPrefilteringJobName = jobName;
  }),

  setSelectedGeoLabelingJobName: action((state, jobName) => {
    state.selectedGeoLabelingJobName = jobName;
  }),

  setGeoInferenceEndpoints: action((state, geoInferenceEndpoints) => {
    state.geoInferenceEndpoints.byId = {};
    state.geoInferenceEndpoints.ids = [];
    geoInferenceEndpoints.forEach((endpoint) => {
      const id = endpoint.arn;
      state.geoInferenceEndpoints.byId[id] = endpoint;
      state.geoInferenceEndpoints.ids.push(id);
    });
  }),
  addGeoInferenceEndpoint: action((state, geoInferenceEndpoint) => {
    const id = geoInferenceEndpoint.arn;
    state.geoInferenceEndpoints.byId[id] = geoInferenceEndpoint;
    state.geoInferenceEndpoints.ids.push(id);
  }),
  setAuthenticationState: action((state, authenticationState) => {
    state.internal.auth = authenticationState;
  }),
};

export const applicationStateAndActions: ApplicationStateAndActions = {
  ...applicationState,
  ...applicationActions,
};

const store = createStore(applicationStateAndActions);

const { useStoreActions, useStoreState, useStoreDispatch, useStore } =
  createTypedHooks<ApplicationStateAndActions>();

export { useStoreActions, useStoreState, useStoreDispatch, useStore, store };
